import { useCallback, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Helmet } from "react-helmet-async";
import { Loader2, PlusIcon } from "lucide-react";
import { toast } from "sonner";

import { PageHeader } from "@/components/ui/page-header";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { CuratorAccessAlert } from "@/components/portal/lms/CuratorGate";
import { CycleCreateDialog } from "@/components/portal/lms/CycleCreateDialog";
import { useCategoryNames } from "@/components/portal/lms/useCategoryNames";
import { useReviewDirectory } from "@/components/portal/lms/useReviewDirectory";
import { usePortalData } from "@/components/portal/PortalDataProvider";
import { useAuth } from "@/contexts/AuthContext";
import { useLmsCuratorAccess } from "@/hooks/useLmsCuratorAccess";
import type { CategoryHierarchyNode } from "@/services/api/categories";
import { lmsCyclesApi, type LmsCycle } from "@/services/api/lms";

export const Route = createFileRoute("/portal/lms/cycles")({
  component: LmsCyclesPage,
});

/**
 * Test cycles (`/portal/lms/cycles`). Every member who can open /portal/lms
 * sees the org's cycles (dp's list is open to any human member). Creating a
 * cycle, and deactivating one, shows only to a curator of that cycle's category
 * (`useLmsCuratorAccess`) — dp's per-category curator gate enforces both. If
 * the curator read fails the list still renders, beside a retryable alert.
 */
function LmsCyclesPage() {
  const { token } = useAuth();
  const { selectedOrganisation } = usePortalData();
  const orgId = selectedOrganisation?.organisationId ?? "";
  const queryClient = useQueryClient();
  const directory = useReviewDirectory();
  const categoryName = useCategoryNames();
  const access = useLmsCuratorAccess();
  const { canCurate } = access;
  const curatorFilter = useCallback((node: CategoryHierarchyNode) => canCurate(node.id), [canCurate]);

  const [creating, setCreating] = useState(false);
  const [confirming, setConfirming] = useState<LmsCycle | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const cyclesKey = ["lms", "cycles", orgId] as const;
  const cycles = useQuery({
    queryKey: cyclesKey,
    queryFn: () => lmsCyclesApi.listCycles(token ?? ""),
    enabled: !!token && !!orgId,
  });

  const deactivate = async (cycle: LmsCycle) => {
    setConfirming(null);
    setBusy(cycle.id);
    setError(null);
    try {
      await lmsCyclesApi.deactivateCycle(token ?? "", cycle.id);
      await queryClient.invalidateQueries({ queryKey: cyclesKey });
      toast.success(`Deactivated "${cycle.name}".`);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not deactivate the cycle");
    } finally {
      setBusy(null);
    }
  };

  const items = cycles.data?.items ?? [];

  return (
    <div className="space-y-4" data-testid="lms-cycles-page">
      <Helmet>
        <title>Test cycles | LMS</title>
      </Helmet>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <PageHeader
          title="Test cycles"
          description="Each active cycle assigns one published test of its category to every member of its team, once a month."
        />
        {access.curatesAny && (
          <Button onClick={() => setCreating(true)} data-testid="lms-cycles-create">
            <PlusIcon className="mr-2 h-4 w-4" /> New cycle
          </Button>
        )}
      </div>

      <CuratorAccessAlert access={access} />

      {error && (
        <p className="text-sm text-destructive" role="alert" data-testid="lms-cycles-action-error">
          {error}
        </p>
      )}

      {cycles.isLoading ? (
        <div className="flex justify-center py-12 text-muted-foreground">
          <Loader2 className="h-6 w-6 animate-spin" />
        </div>
      ) : cycles.error ? (
        <p className="text-sm text-destructive" data-testid="lms-cycles-error">
          {(cycles.error as Error).message}
        </p>
      ) : items.length === 0 ? (
        <p className="text-sm text-muted-foreground" data-testid="lms-cycles-empty">
          No test cycles yet.
        </p>
      ) : (
        <Table data-testid="lms-cycles-table">
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Category</TableHead>
              <TableHead>Team</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="w-[140px]" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((cycle) => (
              <TableRow key={cycle.id} data-testid="lms-cycles-row" data-cycle-id={cycle.id}>
                <TableCell className="font-medium">{cycle.name}</TableCell>
                <TableCell>{categoryName(cycle.topic_category_id)}</TableCell>
                <TableCell>{directory.teamName(cycle.team_id)}</TableCell>
                <TableCell>
                  <Badge variant={cycle.is_active ? "success" : "secondary"} data-testid="lms-cycles-status">
                    {cycle.is_active ? "Active" : "Inactive"}
                  </Badge>
                </TableCell>
                <TableCell className="text-right">
                  {cycle.is_active && canCurate(cycle.topic_category_id) && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setConfirming(cycle)}
                      disabled={busy !== null}
                      data-testid={`lms-cycles-deactivate-${cycle.id}`}
                    >
                      {busy === cycle.id && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                      Deactivate
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <CycleCreateDialog
        token={token ?? ""}
        organisationId={orgId}
        open={creating}
        curatorFilter={curatorFilter}
        onClose={() => setCreating(false)}
        onCreated={(cycle) => {
          setCreating(false);
          toast.success(`Cycle "${cycle.name}" created.`);
          queryClient.invalidateQueries({ queryKey: cyclesKey });
        }}
      />

      <AlertDialog open={confirming !== null} onOpenChange={(o) => !o && setConfirming(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Deactivate “{confirming?.name}”?</AlertDialogTitle>
            <AlertDialogDescription>
              No further monthly assignments are made. Tests already assigned stay assigned.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => confirming && deactivate(confirming)}
              data-testid="lms-cycles-deactivate-confirm"
            >
              Deactivate
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
